/**
 * Editing a ledger that cannot be edited.
 *
 * RETIRED THE SAME DAY IT SHIPPED, AND KEPT FOR THE ROWS IT WROTE. Tim, a few hours after asking
 * for the editing: "i understand the purpose of a ledger, but I need to clean up entries in the
 * faab ledger with perma-delete to remove the items I added manually because it wasn't wired up
 * correctly originally." He was right, and the note below is where the reasoning went wrong: the
 * trail had to exist, but it did not have to be the ledger. It is the audit log now, and a
 * correction is a deletion — see `firestore.rules` and `ui/Faab.tsx`.
 *
 * `voidedIds` and `isVoid` are still wired, because the reversing rows this wrote are in the live
 * ledger and have to keep reading as pairs. `voidEntry`, `amendEntries`, `voidId`, `amendId` and
 * `voidable` are not called any more; they stay as the record of what those rows are, and their
 * tests stay with them. Nothing new should call them.
 *
 * What follows is the original note.
 *
 * Tim, 9 September: "allow for admin/commish to edit the FAAB ledger directly."
 *
 * WHY THIS IS NOT AN EDIT. `firestore.rules` says, on `faab` and on nothing else:
 *
 *     allow create: if isCommish();
 *     allow update, delete: if false;
 *
 * and the comment above it gives the reason — "a FAAB balance is contested often enough that the
 * audit trail matters more than the number, and an edited ledger has no trail". That is not an
 * oversight to route around. A league that argues about money wants to be able to ask, in February,
 * what the number was in August and who changed it, and a document that can be overwritten cannot
 * answer that question about itself.
 *
 * So this does what a bookkeeper does with a wrong line: it writes a second line of equal and
 * opposite amount. The mistake nets to nothing, the balance comes out right, and BOTH halves stay
 * on the page — the original struck through, the reversal stacked under it saying who cancelled it
 * and why. An amendment is that plus a replacement row, so "change ₣22 to ₣25" is three visible
 * facts rather than one silent one.
 *
 * From behind the buttons it is an edit: press Delete and the line stops counting, press Edit and
 * the figure changes. What it leaves behind is a history rather than a revision, which is the
 * property the rule was protecting.
 *
 * THE ID IS DERIVED FROM THE ROW IT REVERSES, which is what makes a double-press safe: `void-x`
 * computed twice is the same id twice, and the second create is REFUSED by the rules rather than
 * subtracting the amount again. Same trick as `chargeId` in `luxuryCharge.ts`, for the same reason.
 * `voidableBy` reads the ledger before offering the button at all, so the ordinary case never
 * reaches a refusal: a line already reversed has no Delete on it.
 */

import type { FaabEntry, GmId } from '../store/types.js';

/** The reversal's id. Derived, so pressing Delete twice cannot subtract twice. */
export const voidId = (id: string): string => `void-${id}`;

/**
 * A replacement's id. Derived from the row it replaces and which replacement it is.
 *
 * THE ORIGINAL'S ID IS THE PREFIX, and that is load-bearing rather than tidy. Other parts of the app
 * attribute a row to the thing that caused it by matching the front of its id — `tradeFaab.ts` asks
 * whether an entry belongs to a given trade and a given man that way. Written `amend-<id>-1` the
 * replacement fell outside that match, so amending a posted trade row made the reconciler read the
 * trade as unposted and offer to pay the man a second time. As a suffix it stays inside every such
 * match for free, and it still reads as a chain: `...-a1-a1` is an amended amendment.
 */
export const amendId = (id: string, seq: number): string => `${id}-a${seq}`;

/** Reversals carry `voids`; everything else is a movement somebody meant. */
export const isVoid = (e: FaabEntry): boolean => typeof e.voids === 'string' && e.voids !== '';

/** Every entry id that some reversal in this ledger cancels. */
export function voidedIds(entries: readonly FaabEntry[]): Set<string> {
  const out = new Set<string>();
  for (const e of entries) if (isVoid(e)) out.add(e.voids!);
  return out;
}

/**
 * May this row be reversed?
 *
 * No for a reversal — cancelling a cancellation is a knot, and the honest way back is a fresh
 * entry. No for a row already reversed, which is the case the derived id would otherwise refuse at
 * the server after the button had already promised something. And no for a row that is not in the
 * ledger at all, which is how a stale screen asks.
 */
export function voidable(id: string, entries: readonly FaabEntry[]): boolean {
  const row = entries.find((e) => e.id === id);
  if (!row || isVoid(row)) return false;
  return !voidedIds(entries).has(id);
}

/**
 * The reversal.
 *
 * `kind` is the ORIGINAL's kind, not `adjustment`. A reversed luxury-tax row has to keep reading as
 * luxury tax, because `taxCharges` sums the ledger's `luxury-tax` rows to work out what a GM has
 * already paid — filing the reversal as an adjustment would leave that sum saying he still owes it
 * and produce a charge on the next run. The same holds for `trade`. A reversal that hides from the
 * arithmetic it is part of is a reversal that gets undone by the app a minute later.
 */
export function voidEntry(
  orig: FaabEntry,
  by: GmId,
  at: string,
  reason?: string,
): FaabEntry {
  const why = (reason ?? '').trim();
  return {
    id: voidId(orig.id),
    season: orig.season,
    gm: orig.gm,
    amount: -orig.amount,
    kind: orig.kind,
    description: `Reversed: ${orig.description}${why ? ` — ${why}` : ''}`,
    date: at.slice(0, 10),
    recordedBy: by,
    voids: orig.id,
  };
}

/** What an amendment may change. Anything absent is carried over from the row being replaced. */
export interface Amendment {
  amount?: number;
  description?: string;
  gm?: GmId;
  date?: string;
}

/**
 * An amendment is a reversal plus a replacement, in that order.
 *
 * Both are returned together and the caller writes them in order, because the two halves are one
 * correction: a reversal that lands without its replacement reads as "that never happened", which
 * is wrong but not misleading, whereas a replacement that lands without its reversal DOUBLE-COUNTS.
 * Cheap half first.
 *
 * The replacement's `voids` is deliberately absent. It is a movement the league means, not a
 * cancellation, and it must be voidable and amendable in its own right — which is what the seq is
 * for: amending an amendment writes `x-a1-a1`, which reads as a chain anybody can follow backwards.
 */
export function amendEntries(
  orig: FaabEntry,
  patch: Amendment,
  by: GmId,
  at: string,
  entries: readonly FaabEntry[],
): [FaabEntry, FaabEntry] {
  const seq = entries.filter((e) => e.id.startsWith(`${orig.id}-a`)).length + 1;
  const replacement: FaabEntry = {
    id: amendId(orig.id, seq),
    season: orig.season,
    gm: patch.gm ?? orig.gm,
    amount: patch.amount ?? orig.amount,
    kind: orig.kind,
    description: (patch.description ?? orig.description).trim() || orig.description,
    date: patch.date ?? orig.date,
    recordedBy: by,
  };
  return [voidEntry(orig, by, at, 'amended'), replacement];
}
